import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Box, Stack, Button } from "@chakra-ui/react";
import { useCardProvider } from "utils";
import { TOSConsent, TOSStatusError, useTOSGatedAction } from "components/JustInTimeTOS";
import BankAccountList from "./BankAccountList";
import { AddPaypalButton } from "components/Paypal";
import { useGenerateUrlAndRedirectUser } from "containers/Pages/PlaidACH/useGenerateUrlAndRedirectUser";
import BankAccountForm from "./BankAccountForm";

/**
 * Component for adding payment method content.
 * @param {Object} props - The props of the component.
 * @param {Function} props.onComplete - Callback function when adding payment method is complete.
 * @param {Function} props.onCancel - Callback function when adding payment method is canceled.
 * @param {Object} [props.chooserConfig={}] - Configuration for payment method chooser.
 * @param {Object} [props.paymentMethodDescriptions={}] - Descriptions for payment methods.
 * @param {string} props.description - Description of the payment method.
 * @param {string} props.path - Path for navigation.
 * @param {Function} [props.onBankAccountClick] - Optional.  Handle bank account selection by user
 * @returns {JSX.Element} Rendered component.
 * @description Gates itself on just-in-time ToS consent rather than taking a `requireTOS` prop.
 * All three of its callers are payment surfaces in scope, so a prop would carry no information, a
 * fourth caller added later would silently default to ungated on a payment path, and it has no
 * out-of-scope callers to capture by accident. The shared leaves below it (AddPaypalButton,
 * AddPlaidACHButton) also render on payout and Deposit, so they keep the opt-in prop. Nor is it
 * scoped to an integrator - see CheckoutContainer for why that is a no-op elsewhere.
 */
const AddPaymentMethodContent = ({
  onComplete,
  onCancel,
  chooserConfig = {},
  providerDescriptions = {},
  description,
  path,
  onBankAccountClick,
}) => {
  const { Provider: AddCard } = useCardProvider();
  const { addACH = false, addCC = false, addPaypal = false } = chooserConfig;
  const [selectedBankAccountCountry, setSelectedBankAccountCountry] = useState(null);
  const history = useHistory();
  const { onGenerateUrlAndRedirectUser } = useGenerateUrlAndRedirectUser();
  // Only the non-addCC branch below renders a consent block, a status error or a gated departure -
  // when addCC is set, the Rebilly form owns its own gate - so gating here would subscribe a second
  // observer to the status query for nothing.
  const { run, error: tosError } = useTOSGatedAction({
    enabled: !addCC,
    flowLoc: "ADD_PM.BANK_COUNTRY.SIGN_TOS",
  });

  /**
   * Handles bank country selection. Both country types are gated CTAs: plaid leaves the widget,
   * and masspay-form reveals <BankAccountForm />, which creates the payment method inline
   * (startCreateTiliaBankAccount) - so letting that through ungated would record no consent at all
   * on a surface that is rendering a consent block. `run` owns the sign (see useTOSGatedAction)
   * because this is BankAccountList's `onCountryChange`, which neither awaits nor catches.
   * @param {Object} countryOption - The selected country option.
   */
  const handleBankCountrySelect = (countryOption) => {
    if (countryOption.type === "plaid") {
      // Recorded up front: for plaid the state write only tracks the dropdown, so a blocked click
      // must not also make the dropdown look broken.
      setSelectedBankAccountCountry(countryOption);
      return run(onGenerateUrlAndRedirectUser);
    }
    // For masspay-form the state write IS the departure - it is what renders the form - so it
    // happens inside `run`, after the sign. CountrySelector holds its own selected value, so the
    // dropdown still shows the choice while a blocked click waits on consent.
    return run(() => setSelectedBankAccountCountry(countryOption));
  };

  if (selectedBankAccountCountry && selectedBankAccountCountry.type === "masspay-form") {
    if (path) {
      setTimeout(() => {
        // moving this to next-tick to avoid a state update error issue
        history.push({
          pathname: `${path}/masspay-form`,
          state: {
            countryISO: selectedBankAccountCountry.ISO,
            currency: selectedBankAccountCountry.currency,
          },
        });
      }, 0);
    } else {
      return (
        <BankAccountForm
          countryISO={selectedBankAccountCountry.ISO}
          currency={selectedBankAccountCountry.currency}
        />
      );
    }
  }

  return (
    <Box py="10">
      {addCC ? (
        <AddCard
          onComplete={onComplete}
          onCancel={onCancel}
          config={chooserConfig}
          requireTOS
        />
      ) : (
        // The two addCC branches are mutually exclusive, so this one needs its own consent block:
        // the Rebilly form's block is not mounted here, and without one this surface would be
        // permanently blocked with no checkbox anywhere to tick.
        <Box>
          <Box>{description}</Box>
          {/* Spacing above instead of below, same idiom as the Rebilly form: the gap under this
              block is already the buttons' own mt="7", and the default pb stacked a second 1rem on
              top of it - leaving the copy bunched under the description with a 2.75rem void before
              the CTAs it gates. pt matches that mt so the block reads as equidistant, and both
              land inside the <Collapse>, so an already-signed user still sees exactly the original
              description -> buttons spacing. */}
          <TOSConsent requireTOS pt={7} pb={0} />
          {/* This branch has no EmptyState and no other error surface, so without this a status
              failure is silent in every direction: both departures refuse and nothing says why. */}
          {tosError && <TOSStatusError />}
          <Stack spacing="4" mt="7">
            {addPaypal && (
              <AddPaypalButton
                newUI={true}
                description={providerDescriptions.paypal}
                requireTOS
              />
            )}
            {addACH && (
              <BankAccountList
                onCountryChange={handleBankCountrySelect}
                onBankAccountClick={onBankAccountClick}
                description={providerDescriptions.banking}
                requireTOS
              />
            )}
          </Stack>
          <Box mt="16">
            <Button data-testid="add-pm-cancel-button" onClick={onCancel} width="100%">
              <FormattedMessage defaultMessage="Cancel" description="button copy" />
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddPaymentMethodContent;
